import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, Loader2, Save, ShieldCheck } from "lucide-react";

export default function AccountPassword() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ c: false, n: false, cc: false });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return toast.error("Le nuove password non coincidono");
    if (form.next.length < 6) return toast.error("Nuova password troppo corta (min 6 caratteri)");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success(
        data.email_sent
          ? "Password aggiornata. Ti abbiamo inviato un'email di conferma."
          : "Password aggiornata. (Conferma email in arrivo appena attiviamo Resend.)"
      );
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="account-password-form">
      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#1D4ED8]" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-lg">Cambio Password</h2>
            <p className="text-sm text-zinc-500">Scegli una password forte e non condividerla con nessuno.</p>
          </div>
        </div>

        <div className="space-y-4 max-w-lg">
          <PwField
            label="Password attuale"
            value={form.current}
            onChange={upd("current")}
            show={show.c}
            onToggle={() => setShow({ ...show, c: !show.c })}
            testid="password-current"
          />
          <PwField
            label="Nuova password"
            value={form.next}
            onChange={upd("next")}
            show={show.n}
            onToggle={() => setShow({ ...show, n: !show.n })}
            testid="password-new"
            hint="Minimo 6 caratteri."
          />
          <PwField
            label="Conferma nuova password"
            value={form.confirm}
            onChange={upd("confirm")}
            show={show.cc}
            onToggle={() => setShow({ ...show, cc: !show.cc })}
            testid="password-confirm"
          />
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={
            saving ||
            !form.current ||
            !form.next ||
            form.next.length < 6 ||
            form.next !== form.confirm
          }
          data-testid="password-save-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#1D4ED8] text-white font-semibold text-sm hover:bg-[#1E3A8A] shadow-sm shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Aggiorna password
        </button>
      </div>
    </form>
  );
}

function PwField({ label, value, onChange, show, onToggle, testid, hint }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-800 mb-1.5">{label}</span>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
          <Lock className="w-4 h-4" />
        </div>
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          data-testid={testid}
          className="w-full h-11 pl-9 pr-11 rounded-md border border-[#E8E4D9] bg-white text-sm focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors"
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </label>
  );
}
